import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiGrid, FiList, FiFileText, FiEdit, FiColumns, FiTrash2, FiEye, FiAlertTriangle, FiDownload, FiFilter, FiCheck } from 'react-icons/fi';
import { LoadingGrid } from '../../components/UI/Loading';
import DownloadReportModal from '../../components/UI/Modal/DownloadReportModal';
import ConfirmModal from '../../components/UI/Modal/ConfirmModal';
import { productosService, reportesService } from '../../services';
import { useDebounce } from '../../hooks/useDebounce';

const ITEMS_PER_PAGE = 24;
const TABLE_PAGE_SIZES = [25, 50, 100];

const Productos = () => {
  const navigate = useNavigate();
  const [productos, setProductos] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showIncludeInactive, setShowIncludeInactive] = useState(false);
  const [activeTab, setActiveTab] = useState('ALL');
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const activeTabRef = useRef(activeTab);
  useEffect(() => { activeTabRef.current = activeTab; }, [activeTab]);

  // Wave SVG dinámica
  const menuRef  = useRef(null);
  const sec1Ref  = useRef(null); // AGREGAR
  const sec2Ref  = useRef(null); // BÚSQUEDA
  const sec3Ref  = useRef(null); // VISTAS
  const svgPathRef = useRef(null); // referencia directa al <path> del SVG

  // Infinite scroll state
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Paginación clásica para vista tabla
  const [tablePage, setTablePage] = useState(1);
  const [tablePageSize, setTablePageSize] = useState(TABLE_PAGE_SIZES[0]);

  // Refs para infinite scroll (evitan closures obsoletos en el observer)
  const sentinelRef = useRef(null);
  const skipRef = useRef(0);
  const loadingRef = useRef(false);       // previene doble disparo del observer
  const searchActiveRef = useRef(false);  // desactiva infinite scroll durante búsqueda
  const showIncludeInactiveRef = useRef(showIncludeInactive);
  const prevViewModeTypeRef = useRef('cards'); // 'cards' | 'table'
  const hasLoadedOnceRef = useRef(false); // tras la primera carga nunca se desmonta el input

  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  const productTypes = [
    { id: 'ALL',              label: 'Todos'         },
    { id: 'BETEL',            label: 'Betel'         },
    { id: 'NATURALANDLAB',    label: 'Naturaland'    },
    { id: 'INGREDIENT',       label: 'Ingredientes'  },
    { id: 'PACKAGING_MATERIAL', label: 'Empaques'   },
    { id: 'OTHER_BRANDS',     label: 'Otras Marcas'  },
  ];

  // Wave dinámica: mide las secciones reales y actualiza el SVG directamente
  const buildWavePath = useCallback(() => {
    if (!menuRef.current || !sec1Ref.current || !sec2Ref.current || !sec3Ref.current || !svgPathRef.current) return;
    const menuRect = menuRef.current.getBoundingClientRect();
    const W = menuRect.width;
    if (W === 0) return;

    const VB = 1000;
    const TOP = 3;
    const BOT = 53;
    const CW  = 36;
    const half = CW / 2;
    const scale = VB / W;

    const b1 = (sec1Ref.current.getBoundingClientRect().right - menuRect.left) * scale;
    const b2 = (sec2Ref.current.getBoundingClientRect().right - menuRect.left) * scale;
    const b3 = (sec3Ref.current.getBoundingClientRect().right - menuRect.left) * scale;

    const d = [
      `M 0,${TOP}`,
      `L ${b1 - half},${TOP}`,
      `C ${b1},${TOP} ${b1},${BOT} ${b1 + half},${BOT}`,
      `L ${b2 - half},${BOT}`,
      `C ${b2},${BOT} ${b2},${TOP} ${b2 + half},${TOP}`,
      `L ${b3 - half},${TOP}`,
      `C ${b3},${TOP} ${b3},${BOT} ${b3 + half},${BOT}`,
      `L ${VB},${BOT}`,
    ].join(' ');

    // Actualización directa del DOM — sin pasar por React state
    svgPathRef.current.setAttribute('d', d);
  }, []);

  useEffect(() => {
    // useEffect corre tras el paint: el flex ya tiene sus anchos reales.
    // El doble rAF es seguro extra para layouts complejos con fuentes/imágenes.
    // Si el menú aún no se montó (loading state), no hacer nada.
    // El effect se re-ejecuta cuando isLoading cambie.
    if (!menuRef.current) return;

    buildWavePath();
    const t = setTimeout(buildWavePath, 100);

    const ro = new ResizeObserver(buildWavePath);
    [menuRef, sec1Ref, sec2Ref, sec3Ref].forEach(ref => {
      if (ref.current) ro.observe(ref.current);
    });

    return () => {
      clearTimeout(t);
      ro.disconnect();
    };
  }, [buildWavePath, isLoading]); // ← isLoading: re-ejecuta cuando el menú aparece

  // Cierre de dropdown al hacer clic fuera
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowTypeDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Mantener ref sincronizada con estado
  useEffect(() => {
    showIncludeInactiveRef.current = showIncludeInactive;
  }, [showIncludeInactive]);

  // Reset página tabla cuando cambian los datos
  useEffect(() => {
    setTablePage(1);
  }, [productos]);

  const getImageUrl = (image) => {
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
    const baseUrl = apiUrl.replace(/\/api\/v1\/?$/, '');
    if (image.medium_url) return `${baseUrl}${image.medium_url}`;
    if (image.url) return `${baseUrl}${image.url}`;
    return null;
  };

  const transformProductoFromAPI = useCallback((producto) => {
    const imgs = producto.images || [];
    const primaryImg = imgs.find(i => i.is_primary) || imgs[0] || null;
    return {
    id: producto.id,
    nombreProducto: producto.name || '',
    codigo: producto.code || '',
    codigoBarras: producto.barcode || '',
    categoria: producto.category || '',
    departamento: producto.product_type || '',
    imagen: primaryImg ? getImageUrl(primaryImg) : null,
    precio: producto.sale_price || 0,
    precioDistribuidor: producto.wholesale_price || 0,
    costo: producto.cost_price || 0,
    stock: producto.current_stock || 0,
    stockMinimo: producto.min_stock || 0,
    stockMaximo: producto.max_stock || 0,
    status: producto.status || 'active'
    };
  }, []);

  const applyInactiveFilter = (list) => {
    if (!showIncludeInactiveRef.current) {
      return list.filter(p => p.status !== 'DISCONTINUED' && p.status !== 'INACTIVE');
    }
    return list;
  };

  // ─── Carga inicial / reset ─────────────────────────────────────────────────
  const loadProductos = useCallback(async (isTableMode = false) => {
    searchActiveRef.current = false;
    setIsLoading(true);
    setError(null);
    setHasMore(false);
    skipRef.current = 0;
    loadingRef.current = false;

    try {
      const params = {
        skip: 0,
        limit: isTableMode ? 1000 : ITEMS_PER_PAGE,
      };
      if (activeTabRef.current !== 'ALL') params.product_type = activeTabRef.current;
      const result = await productosService.getProductos(params);

      if (result.success) {
        const raw = result.data || [];
        const transformed = applyInactiveFilter(raw.map(transformProductoFromAPI));
        setProductos(transformed);
        hasLoadedOnceRef.current = true;

        if (!isTableMode) {
          skipRef.current = ITEMS_PER_PAGE;
          setHasMore(raw.length === ITEMS_PER_PAGE);
        }
      } else {
        setError(result.message || 'Error al cargar productos');
        setProductos([]);
        hasLoadedOnceRef.current = true;
      }
    } catch (err) {
      setError('Error de conexión al cargar productos');
      setProductos([]);
      hasLoadedOnceRef.current = true;
    } finally {
      setIsLoading(false);
    }
  }, [transformProductoFromAPI]); // eslint-disable-line

  // ─── Cargar siguiente página (append) ─────────────────────────────────────
  const loadMore = useCallback(async () => {
    if (loadingRef.current || searchActiveRef.current) return;

    loadingRef.current = true;
    setIsLoadingMore(true);

    try {
      const params = { skip: skipRef.current, limit: ITEMS_PER_PAGE };
      if (activeTabRef.current !== 'ALL') params.product_type = activeTabRef.current;
      const result = await productosService.getProductos(params);

      if (result.success) {
        const raw = result.data || [];
        const transformed = applyInactiveFilter(raw.map(transformProductoFromAPI));
        setProductos(prev => [...prev, ...transformed]);
        skipRef.current += ITEMS_PER_PAGE;
        setHasMore(raw.length === ITEMS_PER_PAGE);
      } else {
        setHasMore(false);
      }
    } catch (err) {
      setHasMore(false);
    } finally {
      loadingRef.current = false;
      setIsLoadingMore(false);
    }
  }, [transformProductoFromAPI]); // eslint-disable-line

  // ─── Búsqueda (carga todo, sin infinite scroll) ───────────────────────────
  const performSearch = useCallback(async (query) => {
    searchActiveRef.current = true;
    setIsLoading(true);
    setHasMore(false);
    setError(null);
    skipRef.current = 0;
    loadingRef.current = false;

    try {
      const params = { skip: 0, limit: 1000, search: query };
      if (activeTabRef.current !== 'ALL') params.product_type = activeTabRef.current;
      const result = await productosService.getProductos(params);
      if (result.success) {
        const transformed = applyInactiveFilter((result.data || []).map(transformProductoFromAPI));
        setProductos(transformed);
        hasLoadedOnceRef.current = true;
      } else {
        setError(result.message || 'Error en la búsqueda');
        setProductos([]);
        hasLoadedOnceRef.current = true;
      }
    } catch (err) {
      setError('Error de conexión en la búsqueda');
      setProductos([]);
      hasLoadedOnceRef.current = true;
    } finally {
      setIsLoading(false);
    }
  }, [transformProductoFromAPI]); // eslint-disable-line

  // ─── Efectos de carga ─────────────────────────────────────────────────────

  // Efecto principal: carga inicial + reacciona a cambios de filtros
  useEffect(() => {
    if (debouncedSearchQuery.trim()) {
      performSearch(debouncedSearchQuery);
    } else {
      loadProductos(viewMode === 'table');
    }
    // eslint-disable-next-line
  }, [showIncludeInactive, activeTab, debouncedSearchQuery]);

  // Cambio de viewMode: recargar solo al cruzar la frontera cards ↔ table
  useEffect(() => {
    const currentType = viewMode === 'table' ? 'table' : 'cards';
    const prevType = prevViewModeTypeRef.current;
    prevViewModeTypeRef.current = currentType;

    if (currentType !== prevType && !debouncedSearchQuery.trim()) {
      loadProductos(viewMode === 'table');
    }
    // eslint-disable-next-line
  }, [viewMode]);

  // ─── IntersectionObserver ─────────────────────────────────────────────────
  useEffect(() => {
    if (viewMode === 'table' || !sentinelRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMore();
        }
      },
      { rootMargin: '200px', threshold: 0 }
    );

    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [loadMore, viewMode, hasMore]);

  // ─── Handlers ─────────────────────────────────────────────────────────────

  const handleAddNew = () => navigate('/system/productos/nuevo');
  const handleDownload = () => setShowDownloadModal(true);

  const handleDownloadReport = async ({ startDate, endDate, format }) => {
    setIsDownloading(true);
    try {
      const params = { format, date_from: startDate, date_to: endDate };
      const result = await reportesService.exportarProductos(params);

      if (result.success) {
        const mimeTypes = { csv: 'text/csv', pdf: 'application/pdf', excel: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' };
        const extensions = { csv: 'csv', pdf: 'pdf', excel: 'xlsx' };
        const blob = new Blob([result.data], { type: mimeTypes[format] || 'application/octet-stream' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = startDate && endDate
          ? `productos_${startDate}_${endDate}.${extensions[format]}`
          : `productos_todos_${new Date().toISOString().split('T')[0]}.${extensions[format]}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        setShowDownloadModal(false);
        alert(`Reporte descargado exitosamente en formato ${format.toUpperCase()}`);
      } else {
        throw new Error(result.message || 'Error al generar el reporte');
      }
    } catch (err) {
      console.error('Error al descargar reporte:', err);
      alert('Error al descargar el reporte. Por favor intente nuevamente.');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleSearchChange = (e) => setSearchQuery(e.target.value);
  const handleClearSearch = () => setSearchQuery('');
  const handleClearError = () => { setError(null); loadProductos(viewMode === 'table'); };
  const handleDeleteClick = (e, producto) => { e.stopPropagation(); setProductToDelete(producto); setShowDeleteModal(true); };
  const handleCancelDelete = () => { setShowDeleteModal(false); setProductToDelete(null); };
  const handleToggleInactive = () => setShowIncludeInactive(prev => !prev);

  const handleConfirmDelete = async () => {
    if (!productToDelete) return;
    setIsDeleting(true);
    try {
      const result = await productosService.softDeleteProducto(productToDelete.id, true);
      if (result.success) {
        setSuccessMessage(`Producto "${productToDelete.nombreProducto}" eliminado correctamente`);
        loadProductos(viewMode === 'table');
        setTimeout(() => setSuccessMessage(null), 5000);
      } else {
        setError(result.message || 'Error al eliminar el producto');
      }
    } catch (err) {
      setError('Error inesperado al eliminar el producto');
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
      setProductToDelete(null);
    }
  };

  const formatPrice = (price) => {
    if (!price) return '$0.00';
    return `${parseFloat(price).toFixed(2)}`;
  };

  const translateCategory = (category) => {
    const categoryMap = {
      'CAPSULES': 'CÁPSULAS', 'POWDERS': 'POLVOS', 'OILS': 'ACEITES',
      'TEAS': 'INFUSIONES', 'CREAMS': 'CREMAS', 'LEAVES': 'HOJAS',
      'LIQUIDS': 'LÍQUIDOS', 'SUPPLEMENTS': 'SUPLEMENTOS', 'TINCTURES': 'TINTURAS',
      'TABLETS': 'TABLETAS', 'EXTRACTS': 'EXTRACTOS', 'HERBS': 'HIERBAS',
      'VITAMINS': 'VITAMINAS', 'MINERALS': 'MINERALES', 'SUPPLIES': 'INSUMOS',
      'PACKAGING': 'EMPAQUE', 'OTHER': 'OTROS'
    };
    return categoryMap[category?.toUpperCase()] || category?.toUpperCase() || '-';
  };

  const getThumbnailType = (departamento) => {
    switch (departamento?.toUpperCase()) {
      case 'BETEL': return 'blue-dark-gradient';
      case 'NATURALANDLAB': return 'green-gradient';
      case 'OTHER_BRANDS': return 'purple-gradient';
      case 'INGREDIENT': return 'orange-gradient';
      case 'PACKAGING_MATERIAL': return 'gray';
      default: return 'blue-dark-gradient';
    }
  };

  // Solo mostrar pantalla de carga completa en la primera carga (antes de que llegue
  // cualquier dato). Una vez que hasLoadedOnceRef es true, el layout permanece siempre
  // montado para que el input de búsqueda nunca pierda el foco.
  if (!hasLoadedOnceRef.current && isLoading) {
    return <LoadingGrid message="Cargando productos..." />;
  }

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="productos-page">
      {/* Success message */}
      {successMessage && (
        <div className="alert alert--success" style={{ marginBottom: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>{successMessage}</span>
            <button onClick={() => setSuccessMessage(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px' }}>×</button>
          </div>
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="alert alert--danger">
          <FiFileText className="alert-icon" />
          <div className="alert-content">
            <strong>Error:</strong> {error}
            <button className="btn btn--ghost btn--sm" onClick={handleClearError} style={{ marginLeft: '10px' }}>Reintentar</button>
          </div>
        </div>
      )}

      <h1 className="page-title">PRODUCTOS</h1>

      {/* Menú de acciones */}
      <div className="page-header-with-menu">
        <div className="action-menu" ref={menuRef}>
          {/* Wave SVG dinámica — path calculado según anchos reales de sección */}
          <svg
            className="action-bar-wave"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 1000 56"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            <defs>
              <linearGradient id="waveGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%"   stopColor="#a64b95" stopOpacity="0.75" />
                <stop offset="45%"  stopColor="#c975b9" stopOpacity="0.75" />
                <stop offset="100%" stopColor="#7e2371" stopOpacity="0.75" />
              </linearGradient>
            </defs>
            <path
              ref={svgPathRef}
              d="M 0,3 L 80,3 C 110,3 110,53 140,53 L 500,53 C 530,53 530,3 560,3 L 720,3 C 750,3 750,53 780,53 L 1000,53"
              fill="none"
              stroke="url(#waveGrad)"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>

          <button className="menu-item menu-item--first" ref={sec1Ref} onClick={handleAddNew}>
            <span className="add-btn-plus">+</span>
            <span className="add-btn-label">AGREGAR</span>
          </button>

          <div className="menu-item menu-item--search" ref={sec2Ref}>
            <input
              type="text"
              className="search-input"
              placeholder="Buscar producto..."
              value={searchQuery}
              onChange={handleSearchChange}
            />
            {isLoading && searchQuery && (
              <span style={{ fontSize: '11px', color: '#999', marginLeft: '6px', whiteSpace: 'nowrap' }}>buscando...</span>
            )}
          </div>


          <button className="menu-item menu-item--download" onClick={handleDownload}>
            DESCARGAR LISTA ↓
          </button>

          <div className="menu-item menu-item--views" ref={sec3Ref}>
            <button className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`} onClick={() => setViewMode('grid')} title="Vista cuadrícula"><FiGrid /></button>
            <button className={`view-btn ${viewMode === 'list' ? 'active' : ''}`} onClick={() => setViewMode('list')} title="Vista lista"><FiList /></button>
            <button className={`view-btn ${viewMode === 'table' ? 'active' : ''}`} onClick={() => setViewMode('table')} title="Vista tabla"><FiColumns /></button>
          </div>

          <div className="menu-item menu-item--checkbox">
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input type="checkbox" checked={showIncludeInactive} onChange={handleToggleInactive} style={{ cursor: 'pointer' }} />
              <span style={{ fontSize: '12px' }}>Inactivos</span>
            </label>

            <span style={{ width: '1px', background: '#e2e8f0', margin: '4px 6px', alignSelf: 'stretch' }}></span>

            {/* Filtro de tipo */}
            <div className="filter-dropdown-wrapper" ref={dropdownRef}>
              <button
                className={`view-btn ${activeTab !== 'ALL' ? 'active' : ''}`}
                onClick={() => setShowTypeDropdown(prev => !prev)}
                title={`Filtrar: ${productTypes.find(t => t.id === activeTab)?.label}`}
              >
                <FiFilter />
              </button>
              {showTypeDropdown && (
                <div className="filter-popover">
                  {productTypes.map(type => (
                    <button
                      key={type.id}
                      className={`filter-popover-item ${activeTab === type.id ? 'active' : ''}`}
                      onClick={() => { setActiveTab(type.id); setShowTypeDropdown(false); }}
                    >
                      {activeTab === type.id && <FiCheck />}
                      {type.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Exportar */}
            <button className="view-btn" onClick={handleDownload} title="Exportar lista">
              <FiDownload />
            </button>
          </div>
        </div>
      </div>

      {/* ─── Contenido ──────────────────────────────────────────────────────── */}
      {productos.length === 0 && !isLoading && !error ? (
        <div className="empty-state">
          <div className="empty-state-icon"><FiFileText size={48} /></div>
          <h3 className="empty-state-title">
            {searchQuery ? 'No se encontraron productos' : 'No hay productos registrados'}
          </h3>
          <p className="empty-state-description">
            {searchQuery
              ? `No se encontraron productos que coincidan con "${searchQuery}"`
              : 'Comienza agregando tu primer producto al sistema'}
          </p>
          {searchQuery && (
            <p><button className="btn btn--ghost" onClick={handleClearSearch}>Limpiar búsqueda</button></p>
          )}
        </div>

      ) : viewMode === 'table' ? (
        /* ─── Vista Tabla con paginador clásico ───────────────────────────── */
        (() => {
          const totalRows = productos.length;
          const totalPages = Math.ceil(totalRows / tablePageSize) || 1;
          const safeTablePage = Math.min(tablePage, totalPages);
          const startIdx = (safeTablePage - 1) * tablePageSize;
          const endIdx = Math.min(startIdx + tablePageSize, totalRows);
          const pageData = productos.slice(startIdx, endIdx);

          // Generar números de página con elipsis
          const getPageNumbers = () => {
            if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
            const pages = [];
            if (safeTablePage <= 4) {
              pages.push(1, 2, 3, 4, 5, '...', totalPages);
            } else if (safeTablePage >= totalPages - 3) {
              pages.push(1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
            } else {
              pages.push(1, '...', safeTablePage - 1, safeTablePage, safeTablePage + 1, '...', totalPages);
            }
            return pages;
          };

          return (
            <div>
              {/* Info + selector de filas */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', fontSize: '13px', color: '#555' }}>
                <span>
                  Mostrando <strong>{totalRows === 0 ? 0 : startIdx + 1}</strong>–<strong>{endIdx}</strong> de <strong>{totalRows}</strong> productos
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>Filas por página:</span>
                  <select
                    value={tablePageSize}
                    onChange={(e) => { setTablePageSize(Number(e.target.value)); setTablePage(1); }}
                    style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '13px', cursor: 'pointer' }}
                  >
                    {TABLE_PAGE_SIZES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>

              <div className="table-container">
                <table className="data-table data-table--productos">
                  <thead>
                    <tr>
                      <th>CÓDIGO</th>
                      <th>NOMBRE DEL PRODUCTO</th>
                      <th>CATEGORÍA</th>
                      <th className="text-center">PRECIO</th>
                      <th className="text-center">STOCK</th>
                      <th className="text-center">ESTADO</th>
                      <th className="text-center">ACCIONES</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageData.map((producto) => (
                      <tr
                        key={producto.id}
                        style={{ cursor: 'pointer' }}
                        className={producto.status === 'DISCONTINUED' || producto.status === 'INACTIVE' ? 'row-inactive' : ''}
                      >
                        <td onClick={() => navigate(`/system/productos/${producto.id}`)}>{producto.codigo || '-'}</td>
                        <td onClick={() => navigate(`/system/productos/${producto.id}`)}><strong>{producto.nombreProducto}</strong></td>
                        <td onClick={() => navigate(`/system/productos/${producto.id}`)}>{translateCategory(producto.categoria)}</td>
                        <td className="text-center" onClick={() => navigate(`/system/productos/${producto.id}`)}>{formatPrice(producto.precio)}</td>
                        <td className="text-center">
                          {producto.stock !== undefined ? (
                            <span className={`badge ${producto.stock <= producto.stockMinimo ? 'badge-warning' : 'badge-success'}`}>
                              {producto.stock}
                            </span>
                          ) : '-'}
                        </td>
                        <td className="text-center" onClick={() => navigate(`/system/productos/${producto.id}`)}>
                          <span className={`badge badge-${
                            producto.status === 'ACTIVE' || producto.status === 'active' ? 'success' :
                            producto.status === 'DISCONTINUED' ? 'danger' :
                            producto.status === 'OUT_OF_STOCK' ? 'warning' : 'secondary'
                          }`}>
                            {producto.status === 'ACTIVE' || producto.status === 'active' ? 'Activo' :
                             producto.status === 'INACTIVE' ? 'Inactivo' :
                             producto.status === 'DISCONTINUED' ? 'Descontinuado' :
                             producto.status === 'OUT_OF_STOCK' ? 'Agotado' : producto.status}
                          </span>
                        </td>
                        <td className="text-center">
                          <div className="action-buttons" style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                            <button className="btn btn--icon btn--ghost" onClick={(e) => { e.stopPropagation(); navigate(`/system/productos/${producto.id}`); }} title="Ver detalle"><FiEye size={16} /></button>
                            <button className="btn btn--icon btn--ghost" onClick={(e) => { e.stopPropagation(); navigate(`/system/productos/${producto.id}/editar`); }} title="Editar"><FiEdit size={16} /></button>
                            {producto.status !== 'DISCONTINUED' && producto.status !== 'INACTIVE' && (
                              <button className="btn btn--icon btn--ghost btn--danger" onClick={(e) => handleDeleteClick(e, producto)} title="Eliminar"><FiTrash2 size={16} /></button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Paginador */}
              {totalPages > 1 && (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '4px', marginTop: '16px', flexWrap: 'wrap' }}>
                  {/* Anterior */}
                  <button
                    onClick={() => setTablePage(p => Math.max(1, p - 1))}
                    disabled={safeTablePage === 1}
                    style={{
                      padding: '6px 12px', borderRadius: '6px', border: '1px solid #ddd',
                      background: safeTablePage === 1 ? '#f5f5f5' : '#fff',
                      color: safeTablePage === 1 ? '#bbb' : '#333',
                      cursor: safeTablePage === 1 ? 'not-allowed' : 'pointer',
                      fontSize: '13px', fontWeight: '500'
                    }}
                  >
                    ‹ Anterior
                  </button>

                  {/* Números de página */}
                  {getPageNumbers().map((page, idx) =>
                    page === '...' ? (
                      <span key={`ellipsis-${idx}`} style={{ padding: '6px 4px', color: '#999', fontSize: '13px' }}>…</span>
                    ) : (
                      <button
                        key={page}
                        onClick={() => setTablePage(page)}
                        style={{
                          padding: '6px 11px', borderRadius: '6px', border: '1px solid',
                          borderColor: safeTablePage === page ? '#7B2D8E' : '#ddd',
                          background: safeTablePage === page ? '#7B2D8E' : '#fff',
                          color: safeTablePage === page ? '#fff' : '#333',
                          cursor: 'pointer', fontSize: '13px', fontWeight: safeTablePage === page ? '700' : '400',
                          minWidth: '34px'
                        }}
                      >
                        {page}
                      </button>
                    )
                  )}

                  {/* Siguiente */}
                  <button
                    onClick={() => setTablePage(p => Math.min(totalPages, p + 1))}
                    disabled={safeTablePage === totalPages}
                    style={{
                      padding: '6px 12px', borderRadius: '6px', border: '1px solid #ddd',
                      background: safeTablePage === totalPages ? '#f5f5f5' : '#fff',
                      color: safeTablePage === totalPages ? '#bbb' : '#333',
                      cursor: safeTablePage === totalPages ? 'not-allowed' : 'pointer',
                      fontSize: '13px', fontWeight: '500'
                    }}
                  >
                    Siguiente ›
                  </button>
                </div>
              )}
            </div>
          );
        })()

      ) : (
        /* ─── Vista Grid / List con Infinite Scroll ───────────────────────── */
        <>
          <div className={viewMode === 'grid' ? 'content-grid' : 'content-list'}>
            {productos.map((producto) => (
              <div
                key={producto.id}
                className={viewMode === 'grid' ? 'content-card' : 'content-card-list'}
                onClick={() => navigate(`/system/productos/${producto.id}`)}
                style={{ cursor: 'pointer' }}
              >
                {viewMode === 'grid' ? (
                  <>
                    <div className={`content-thumbnail ${getThumbnailType(producto.departamento)}`}>
                      {producto.imagen
                        ? <img src={producto.imagen} alt={producto.nombreProducto} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                        : <span className="logo-text"></span>
                      }
                    </div>
                    <div className="content-info">
                      <div className="content-title">{producto.nombreProducto}</div>
                      <div className="content-subtitle">{translateCategory(producto.categoria)}</div>
                      <div className="content-meta-grid">
                        <span className="meta-item">Precio: {formatPrice(producto.precio)}</span>
                        {producto.stock !== undefined && (
                          <span className={`meta-item ${producto.stock <= producto.stockMinimo ? 'warning' : ''}`}>
                            Stock: {producto.stock}
                          </span>
                        )}
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className={`content-thumbnail-small ${getThumbnailType(producto.departamento)}`}>
                      {producto.imagen
                        ? <img src={producto.imagen} alt={producto.nombreProducto} className="thumbnail-image-small" />
                        : <span className="logo-text-small">IMG</span>
                      }
                    </div>
                    <div className="content-info-list">
                      <div className="content-title">{producto.nombreProducto}</div>
                      <div className="content-subtitle">{translateCategory(producto.categoria)}</div>
                      <div className="content-meta">
                        <span className="meta-item">Precio: {formatPrice(producto.precio)}</span>
                        <span className="meta-item">Código: {producto.codigo}</span>
                        {producto.stock !== undefined && (
                          <span className={`meta-item ${producto.stock <= producto.stockMinimo ? 'warning' : ''}`}>
                            Stock: {producto.stock}
                          </span>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>

          {/* Sentinel + spinner de carga adicional */}
          <div ref={sentinelRef} style={{ height: '1px' }} />

          {isLoadingMore && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '24px', gap: '10px', color: '#666' }}>
              <div className="loading-spinner-single" style={{ width: '24px', height: '24px' }} />
              <span style={{ fontSize: '14px' }}>Cargando más productos...</span>
            </div>
          )}

          {!hasMore && !isLoadingMore && productos.length > 0 && !searchQuery && (
            <div style={{ textAlign: 'center', padding: '20px', color: '#999', fontSize: '13px' }}>
              — {productos.length} productos —
            </div>
          )}
        </>
      )}

      {/* Modales */}
      <DownloadReportModal
        isOpen={showDownloadModal}
        onClose={() => setShowDownloadModal(false)}
        onDownload={handleDownloadReport}
        title="Descargar Reporte de Productos"
        moduleName="productos"
        isLoading={isDownloading}
      />

      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={handleCancelDelete}
        onCancel={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Eliminar Producto"
        message={
          productToDelete ? (
            <div>
              <p>¿Está seguro que desea eliminar el producto <strong>"{productToDelete.nombreProducto}"</strong>?</p>
              <div className="alert alert--warning" style={{ marginTop: '12px', padding: '12px' }}>
                <FiAlertTriangle style={{ marginRight: '8px' }} />
                <span>El producto será marcado como <strong>descontinuado</strong> y no aparecerá en el listado principal. Los registros históricos de ventas se mantendrán.</span>
              </div>
            </div>
          ) : 'Confirmar eliminación'
        }
        confirmText={isDeleting ? 'Eliminando...' : 'Sí, eliminar'}
        cancelText="Cancelar"
        type="danger"
        isLoading={isDeleting}
      />
    </div>
  );
};

export default Productos;
